import RawTextField, { RawTextDefaultProps } from './components/RawTextField.js';
import React__default from 'react';
import { DateFieldDateFormat } from './types.js';
import { DateFieldSize } from './constants.js';

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutProperties(source, excluded) { if (source == null) return {}; var target = _objectWithoutPropertiesLoose(source, excluded); var key, i; if (Object.getOwnPropertySymbols) { var sourceSymbolKeys = Object.getOwnPropertySymbols(source); for (i = 0; i < sourceSymbolKeys.length; i++) { key = sourceSymbolKeys[i]; if (excluded.indexOf(key) >= 0) continue; if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue; target[key] = source[key]; } } return target; }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }
var INVALID_CHARS_REGEX = /[^0-9/]/g;

var removeInvalidChars = function removeInvalidChars(value) {
  return value.replace(INVALID_CHARS_REGEX, '');
};

var MONTH_ONLY_REGEX = /^([0-9]{1,2})(\/?)$/;
var MONTH_AND_DAY_REGEX = /^([0-9]{1,2})\/?([0-9]{1,2})(\/?)$/;
var FULL_DATE_REGEX = /^([0-9]{1,2})\/([0-9]{1,2})\/?([0-9]{1,4})/;

var dateToValue = function dateToValue(date) {
  var dd = date.getUTCDate().toString().padStart(2, '0');
  var mm = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  var yyyy = date.getUTCFullYear();
  return "".concat(mm, "/").concat(dd, "/").concat(yyyy);
};

var YEAR_ONLY_REGEX = /^([0-9]{1,4})(\/?)$/;
var YEAR_AND_MONTH_REGEX = /^([0-9]{1,4})\/?([0-9]{1,2})(\/?)$/;
var FULL_DATE_YEAR_LEADING_REGEX = /^([0-9]{1,4})\/([0-9]{1,2})\/?([0-9]{1,2})/; //Custom Date Formatter in the case of YYYY/MM/DD dates

var formatDateYearFirst = function formatDateYearFirst(value) {
  var numbers = removeInvalidChars(value);

  if (YEAR_ONLY_REGEX.test(numbers)) {
    var match = YEAR_ONLY_REGEX.exec(numbers); // casting to maintain existing logic

    var year = match[1];
    var endSlash = match[2];
    return "".concat(year).concat(endSlash);
  } else if (YEAR_AND_MONTH_REGEX.test(numbers)) {
    var _match = YEAR_AND_MONTH_REGEX.exec(numbers); // casting to maintain existing logic


    var _year = _match[1];
    var month = _match[2];
    var _endSlash = _match[3];
    return "".concat(_year, "/").concat(_endSlash && month.length !== 2 ? "0".concat(month) : month).concat(_endSlash);
  } else if (FULL_DATE_YEAR_LEADING_REGEX.test(numbers)) {
    var _match2 = FULL_DATE_YEAR_LEADING_REGEX.exec(numbers); // casting to maintain existing logic


    var _year2 = _match2[1];
    var _month = _match2[2];
    var day = _match2[3];
    return "".concat(_year2, "/").concat(_month.length === 2 ? _month : "0".concat(_month), "/").concat(day);
  }

  return numbers;
};
var formatDate = function formatDate(value, dateFormat) {
  dateFormat = dateFormat || DateFieldDateFormat.en_US;

  if (value instanceof Date) {
    // eslint-disable-next-line no-console
    console.warn("Warning! We notice that you're setting `value` as a Date object. If you're setting value in state, your end user could run into issues when typing out a date. We recommend setting `value` as a string, and using the fourth Date object parameter returned in `onChange` for your Date-dependent change handler.");
    return dateToValue(value);
  }

  if (dateFormat === DateFieldDateFormat.ja_JP) {
    return formatDateYearFirst(value);
  }

  var numbers = removeInvalidChars(value);

  if (MONTH_ONLY_REGEX.test(numbers)) {
    var match = MONTH_ONLY_REGEX.exec(numbers); // casting to maintain existing logic

    var month = match[1];
    var endSlash = match[2];
    return "".concat(endSlash && month.length !== 2 ? "0".concat(month) : month).concat(endSlash);
  } else if (MONTH_AND_DAY_REGEX.test(numbers)) {
    var _match3 = MONTH_AND_DAY_REGEX.exec(numbers); // casting to maintain existing logic


    var _month2 = _match3[1];
    var day = _match3[2];
    var _endSlash2 = _match3[3];
    return "".concat(_month2.length === 2 ? _month2 : "0".concat(_month2), "/").concat(_endSlash2 && day.length !== 2 ? "0".concat(day) : day).concat(_endSlash2);
  } else if (FULL_DATE_REGEX.test(numbers)) {
    var _match4 = FULL_DATE_REGEX.exec(numbers); // casting to maintain existing logic


    var _month3 = _match4[1];
    var _day = _match4[2];
    var year = _match4[3];
    return "".concat(_month3.length === 2 ? _month3 : "0".concat(_month3), "/").concat(_day.length === 2 ? _day : "0".concat(_day), "/").concat(year);
  } else if (!!Date.parse(value)) {
    return dateToValue(new Date(value));
  }

  return numbers;
};

var DateField = function DateField(_ref) {
  var _ref$dateFormat = _ref.dateFormat,
      dateFormat = _ref$dateFormat === void 0 ? DateFieldDateFormat.en_US : _ref$dateFormat,
      _ref$isResizeable = _ref.isResizeable,
      isResizeable = _ref$isResizeable === void 0 ? RawTextDefaultProps.isResizeable : _ref$isResizeable,
      _ref$size = _ref.size,
      size = _ref$size === void 0 ? RawTextDefaultProps.size : _ref$size,
      _ref$isRounded = _ref.isRounded,
      isRounded = _ref$isRounded === void 0 ? RawTextDefaultProps.isRounded : _ref$isRounded,
      props = _objectWithoutProperties(_ref, ["dateFormat", "isResizeable", "size", "isRounded"]);

  return /*#__PURE__*/React__default.createElement(RawTextField, _extends({}, props, {
    type: "text",
    isResizeable: isResizeable,
    size: size,
    isRounded: isRounded,
    value: formatDate(props.value, dateFormat),
    onChange: function onChange(value, event) {
      var stringDate = formatDate(value, dateFormat);
      var dateValue;

      if ( // 8 or 10 digit length to allow for two-digit years
      // Example: `01/01/2010` and `01/01/10` would both work
      (stringDate.length === 8 || stringDate.length === 10) && !isNaN(Date.parse(stringDate))) {
        dateValue = new Date(stringDate);
      }

      props.onChange(stringDate, value, event, dateValue);
    },
    maxLength: 10 // 12/45/7890
    ,
    pattern: "[0-9]*",
    __focusBehavior: "default"
  }));
};

DateField.Sizes = DateFieldSize;

export { DateField as default, formatDate, formatDateYearFirst };
